import {
  Button,
  ButtonGroup,
  elPb8,
  elRowGap1,
  elRowGap4,
  FlexContainer,
  Loader,
  PersistantNotification,
  TextSM,
  TextXS,
} from '@reapit/elements'
import { useDocumentActivityLog } from '../../api/document-activity'
import { Drawer } from '@reapitform/common-ui'
import { useNavigate } from 'react-router'
import { usePDF } from '@react-pdf/renderer'
import { AgreementActivityLogPDF } from '../pdf/agreement-activity-log'
import React from 'react'
import { useOrganizationContact } from '../../hooks/use-orgazination-contact'

export interface ActivityListModalProps {
  documentId: string
  isOpen: boolean
  isOpenChange?: (isOpen: boolean) => void
  hideViewAgreementButton?: boolean
  hideExportButton?: boolean
  agreementName: string
}

export const ActivityListModal: React.FC<ActivityListModalProps> = ({
  isOpen,
  isOpenChange,
  documentId,
  hideExportButton,
  hideViewAgreementButton,
  agreementName,
}) => {
  const { data, loading, errorString } = useDocumentActivityLog(documentId)
  const organizationData = useOrganizationContact()

  const navigate = useNavigate()
  const [pdfInstance, setPdfDocument] = usePDF()

  const handleDownloadActivity = () => {
    if (!pdfInstance.blob) return
    const pdfURL = URL.createObjectURL(pdfInstance.blob)

    const anchorLinkElement = document.createElement('a')
    anchorLinkElement.href = pdfURL
    anchorLinkElement.rel = 'noopener noreferrer'
    anchorLinkElement.target = '_blank'
    anchorLinkElement.style.display = 'none'
    document.body.appendChild(anchorLinkElement)

    anchorLinkElement.click()

    document.body.removeChild(anchorLinkElement)
    URL.revokeObjectURL(pdfURL)
  }

  React.useEffect(() => {
    if (loading || !data) return
    setPdfDocument(
      <AgreementActivityLogPDF data={data} agreementName={agreementName} organizationData={organizationData ?? {}} />,
    )
  }, [loading, data])

  return (
    <Drawer
      isOpen={isOpen}
      onDrawerClose={() => isOpenChange?.(!isOpen)}
      canDismiss
      title="Activity Log"
      subtitle={data?.propertyAddress}
    >
      <FlexContainer isFlexColumn>
        {loading && <Loader />}
        {!loading && !errorString && (
          <>
            {!(hideExportButton && hideViewAgreementButton) && (
              <ButtonGroup alignment="left" className={elPb8}>
                {!hideViewAgreementButton && (
                  <Button intent="neutral" onClick={() => navigate(`${documentId}/create`)}>
                    View Agreement
                  </Button>
                )}
                {!hideExportButton && (
                  <Button
                    onClick={handleDownloadActivity}
                    disabled={pdfInstance.loading}
                    intent="neutral"
                    buttonIcon={{ icon: 'exportIcon', position: 'left' }}
                  >
                    Export
                  </Button>
                )}
              </ButtonGroup>
            )}
            <FlexContainer isFlexColumn className={elRowGap4}>
              {data?.activities.map((activity, activityIndex) => (
                <FlexContainer key={`${activity.name}-${activityIndex}`} isFlexColumn className={elRowGap1}>
                  <TextXS hasGreyText>{activity.dateTime}</TextXS>
                  <TextSM>{activity.description}</TextSM>
                </FlexContainer>
              ))}
            </FlexContainer>
          </>
        )}
        {errorString && <PersistantNotification>Failed to load the activity log</PersistantNotification>}
      </FlexContainer>
    </Drawer>
  )
}
